/**
 * S1 · The Stats tab, on every level.
 *
 * The same tab whether the target is a sport, a competition node or one match:
 * what stats apply here, where each came from, whether its numbers are still
 * right, and what you can press.
 *
 * Assembled from the kit and carrying no stylesheet of its own — which is the
 * rule the kit sets and which this screen used to break. It had its own 45-line
 * sheet, so it read as bare text on a page while everything around it was a
 * designed product: an unboxed table, counts and navigation run together in one
 * grey sentence, and section headings in raw bold.
 */

import { useCallback, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  Button,
  Card,
  Empty,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Section,
  Stat,
  StatGrid,
  Table,
  Td,
  Th,
  Toolbar,
  Stack,
} from "@/components/kit";
import { DOT_LABEL, ago, loadTab, runNow, type StatLine, type Tab } from "@/lib/statsEngine/api";
import { useApi } from "@/spa/useApi";

/** A stat's freshness, as shape and colour together — never colour alone. */
const TONE: Record<StatLine["status"], "good" | "warn" | "accent" | "neutral" | "bad"> = {
  up_to_date: "good",
  stale: "warn",
  waiting: "accent",
  never: "neutral",
  failed: "bad",
};

export function Dot({ status }: { status: StatLine["status"] }) {
  return (
    <Pill tone={TONE[status]} dot>
      {DOT_LABEL[status]}
    </Pill>
  );
}

function numbersHref(sport: string, line: StatLine) {
  const q = new URLSearchParams({
    key: line.key,
    kind: line.scopeKind,
    ref: line.scopeRef,
  });
  return `/${sport}/stats-engine/numbers?${q}`;
}

function Lines({
  lines,
  sport,
  onRun,
  busy,
  empty,
}: {
  lines: StatLine[];
  sport: string;
  onRun: (line: StatLine) => void;
  busy: string | null;
  empty: { title: string; note: string };
}) {
  if (lines.length === 0) {
    return (
      <Card>
        <Empty title={empty.title} note={empty.note} />
      </Card>
    );
  }
  return (
    <Table>
      <thead>
        <tr>
          <Th>Stat</Th>
          <Th>Gives</Th>
          <Th>Counts over</Th>
          <Th>Lane</Th>
          <Th>Numbers</Th>
          <Th>Last run</Th>
          <Th />
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={`${line.key}:${line.scopeRef}`}>
            <Td strong>
              <Link to={numbersHref(sport, line)}>{line.name}</Link>
              <div className="muted mono">{line.key}</div>
            </Td>
            <Td>{line.gives}</Td>
            <Td>{line.scopeKind}</Td>
            <Td quiet>{line.tier}</Td>
            <Td>
              <Dot status={line.status} />
              {line.status === "stale" && line.asOf && line.reality ? (
                <div className="muted mono">
                  counted {line.asOf[1]}, played {line.reality[1]}
                </div>
              ) : null}
            </Td>
            <Td quiet>
              {ago(line.lastRunAt)}
              {line.lastRunMs !== null ? <div className="muted mono">{line.lastRunMs} ms</div> : null}
            </Td>
            <Td num>
              <Button disabled={busy === line.key} onClick={() => onRun(line)}>
                {busy === line.key ? "Queuing…" : "Run"}
              </Button>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function StatsTabPage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const [params] = useSearchParams();
  const kind = params.get("kind") ?? "sport";
  // The sidebar link knows the sport as `cricket` and has never seen its uuid,
  // so the code is the default target. The API takes either.
  const ref = params.get("ref") ?? sport;
  const load = useCallback(() => loadTab(kind, ref), [kind, ref]);
  const { data, loading, error, reload } = useApi<Tab>(
    load,
    [kind, ref],
    "the admin API is not reachable",
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string>("");

  const run = useCallback(
    async (line: StatLine) => {
      setBusy(line.key);
      setSaid("");
      try {
        const result = await runNow(line.key, line.scopeKind, line.scopeRef);
        setSaid(result.queued ? "Queued." : "Already waiting — nothing is queued twice.");
        reload();
      } catch (cause) {
        setSaid(cause instanceof Error ? cause.message : "could not queue the run");
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;
  if (!data) {
    return <PageHead title="Stats" note={loading ? "Reading…" : ""} />;
  }

  const { summary } = data;
  const behind = summary.stats - summary.up_to_date - summary.waiting - summary.failed;

  return (
    <Stack>
      <PageHead
        title={`Stats · ${data.title}`}
        note="Every stat that applies here, where it came from, and whether its numbers are still right."
        actions={
          <Toolbar>
            <LinkButton to={`/${sport}/stats-engine/runs`}>Runs</LinkButton>
            <LinkButton to={`/${sport}/stats-engine/catalogue`}>Catalogue</LinkButton>
            <LinkButton to={`/${sport}/stats-engine/add`} tone="primary">
              Add a stat
            </LinkButton>
          </Toolbar>
        }
      />

      {/* The four numbers a person actually wants, as four numbers — not as a
          sentence with dots in it. "Behind" is the one worth acting on. */}
      <StatGrid>
        <Stat label="Stats here" value={summary.stats} />
        <Stat label="Up to date" value={summary.up_to_date} note="counted every match played" />
        <Stat label="Behind" value={behind} note={behind ? "matches played since the last count" : "nothing waiting"} />
        <Stat label="Failed" value={summary.failed} note={summary.failed ? "see Runs for why" : "none"} />
      </StatGrid>

      {said ? <Card>{said}</Card> : null}

      <Section
        title="From the sport"
        note={`Declared by the ${data.sportCode} plugin. Always on, everywhere this sport is played.`}
      >
        <Lines
          lines={data.fromSport}
          sport={sport}
          onRun={run}
          busy={busy}
          empty={{
            title: "This sport declares no stats",
            note: "A stat declared in the plugin applies wherever the sport is played, without anyone attaching it.",
          }}
        />
      </Section>

      <Section
        title="Only here"
        note="Attached at this level, and computed for it alone."
      >
        <Lines
          lines={data.onlyHere}
          sport={sport}
          onRun={run}
          busy={busy}
          empty={{
            title: "Nothing attached here",
            note: "Add a stat to compute something for this level that the sport does not compute everywhere.",
          }}
        />
      </Section>
    </Stack>
  );
}
